import cx from 'classnames';
import type { FunctionComponent } from 'preact';
import type { SVGProps } from 'preact/compat';
import styles from './createSvgIcon.module.css';

export type SvgIconProps = Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'viewBox'>;

/**
 * Create an SVG icon with proper styling. The advantage of this over inline SVG imports is that
 * it allows the icons to automatically match the text color (`fill="currentColor"`).
 *
 * This is based on `createFluentIcon` from `@fluentui/react-icons`.
 *
 * @param displayName The name of the icon, used for debugging
 * @param width Original width/height of the icon SVG content in pixels, for correct scaling.
 * @param pathContents `<path>` tag contents
 * @param color Hardcoded color for the icon (defaults to `'currentColor'` i.e. text color).
 * This should only be used for things like error/success/warning icons.
 */
export function createSvgIcon(
  displayName: string,
  width: number,
  // Currently all the icons just have one path (this could be changed to an array or children if needed)
  pathContents: string,
  color?: `#${string}`,
): FunctionComponent<SvgIconProps> {
  const Icon: FunctionComponent<SvgIconProps> = (props) => {
    // Extract `className` to avoid conflicts with `class`.
    // Extract `title` since it doesn't work properly on SVG elements and must be manually handled.
    const { className, title, ...rest } = props;
    const svgProps: SVGProps<SVGSVGElement> = {
      fill: color || 'currentColor',
      'aria-label': title,
      ...rest,
      class: cx(styles.root, className || props.class),
      width,
      height: width,
      viewBox: `0 0 ${width} ${width}`,
      xmlns: 'http://www.w3.org/2000/svg',
    };

    if (!svgProps['aria-label'] && !svgProps['aria-labelledby']) {
      svgProps['aria-hidden'] = true;
    } else {
      svgProps['role'] = 'img';
    }

    return (
      <svg {...svgProps}>
        {title && <title>{title}</title>}
        <path d={pathContents} fill={svgProps.fill} />
      </svg>
    );
  };
  Icon.displayName = displayName;

  return Icon;
}
